/**
 * Usage session tracking service for teacher app.
 * Tracks session start/end and heartbeat for time-spent analytics.
 */

import { getAccessToken } from 'services/authToken';

const USAGE_SESSION_BASE = 'https://v2.infopublisher.in/usage/sessions';
const SESSION_STORAGE_KEY = 'usage_session_id';

function getAuthHeaders() {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Get current timestamp in ISO format (same style as API examples).
 */
function getISOTimestamp() {
  return new Date().toISOString();
}

/**
 * Generate a unique session ID: usage_<timestamp>_<random>
 */
export function generateSessionId() {
  const random = Math.random().toString(36).substring(2, 11);
  return `usage_${Date.now()}_${random}`;
}

/**
 * Get the current session ID from sessionStorage (if any).
 */
export function getCurrentSessionId() {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

/**
 * Store session ID so logout can end the session.
 */
function setStoredSessionId(sessionId) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
  } catch (_) {}
}

/**
 * Clear stored session ID.
 */
export function clearStoredSessionId() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch (_) {}
}

/**
 * Start a usage session.
 * Payload: { sessionId, startedAt, ipAddress, device }
 */
export function startSession(sessionId) {
  const payload = {
    sessionId,
    startedAt: getISOTimestamp(),
    ipAddress: '',
    device: 'web',
  };
  setStoredSessionId(sessionId);
  return fetch(`${USAGE_SESSION_BASE}/start`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn('[UsageSession] start failed:', err);
  });
}

/**
 * End a usage session.
 * Payload: { sessionId, endedAt }
 */
export function endSession(sessionId) {
  if (!sessionId) return Promise.resolve();
  const payload = {
    sessionId,
    endedAt: getISOTimestamp(),
  };
  clearStoredSessionId();
  return fetch(`${USAGE_SESSION_BASE}/end`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn('[UsageSession] end failed:', err);
  });
}

/**
 * End session (for page unload / logout). Uses fetch with keepalive when token is present so we can send Authorization.
 */
export function endSessionBeacon(sessionId) {
  if (!sessionId) return;
  const payload = {
    sessionId,
    endedAt: getISOTimestamp(),
  };
  clearStoredSessionId();
  const token = getAccessToken();
  if (token && typeof fetch !== 'undefined') {
    fetch(`${USAGE_SESSION_BASE}/end`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(
      `${USAGE_SESSION_BASE}/end`,
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );
  }
}

/**
 * Send heartbeat for active session.
 * Payload: { sessionId, timestamp }
 */
export function sendHeartbeat(sessionId) {
  if (!sessionId) return Promise.resolve();
  const payload = {
    sessionId,
    timestamp: getISOTimestamp(),
  };
  return fetch(`${USAGE_SESSION_BASE}/heartbeat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn('[UsageSession] heartbeat failed:', err);
  });
}
